<template>
  <div>
    <app-action-bar
      :menu-items="actionBarItems"
      :selected-items="selection"
      data-locator="multi-select-bar" 
      @clear-selection="onClearSelection"
    >
      <app-action-bar-list-item 
        v-for="item in actionBarItems"
        :item="item"
        :key="item.name"
      >
        <PolyOfficeMultiSelectBarButtonExport
          v-if="item.name === 'poly'"
          :selectionSize="documentSelectionSize"
          :selectionCount="selection.length"
          @polyOfficeOpenDialog="polyOfficeOpenDialogHandler"
        />
      </app-action-bar-list-item>
    </app-action-bar>

    <BibleGenerateDialog v-if="openBibleGenerateDialog"
                         :mnemo="$route.params.mnemo"
                         initial-tab="signatures"
                         :selected-signatures="selection"
                         :poll-after-post="false"
                         display-tree-structure
                         @close="onBibleGenerateDialogClose"
                         @selectedSignature="onSelectedSignatures"
    />

    <SignedDocumentsDownloadDialog v-if="openSingleFileDownloadDialog"
                                   :signedDocument="selection[0]"
                                   @close="openSingleFileDownloadDialog = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import { SignedEnvExport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeMultiSelectBarButtonExport from '@/common/polyOffice/PolyOfficeMultiSelectBarButtonExport.vue'
import { getRoomRouteType } from '@/common/utils/rooms'
import { toast } from '@/common/utils/toast'
import BibleGenerateDialog from '@/project/bibles/dialogs/bible-generation/BibleGenerateDialog.vue'
import { ROOM_BIBLES_ROUTE_NAME , ARCHIVES_ADD_ROUTE_NAME } from '@/router'
import { GET_EXPORT_IFRAME } from '@/store/modules/poly-office/action_types'

import SignedDocumentsDownloadDialog from './dialogs/SignedDocumentsDownloadDialog.vue'

export default {
  name: 'SignedDocumentsActionBar',
  components: {
    BibleGenerateDialog,
    PolyOfficeMultiSelectBarButtonExport,
    SignedDocumentsDownloadDialog,
  },
  emits: ['deselect-all', 'exportToImanage', 'selectedSignature'],
  inject: [
    'openDeleteOriginalsDialog',
    'openExportToIManageDialog',
    'openShareOriginalsPane',
  ],
  props: {
    selection: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      openBibleGenerateDialog: false,
      openSingleFileDownloadDialog: false,
    }
  },
  computed: {
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('room', ['roomMnemo']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    ...mapState('room', ['currentRoom']),
    actionBarItems () {
        return [
        {
          action: () => { this.onDownloadSignedDocumentsClick() },
          icon: 'download',
          name: 'download',
          title: this.$t('common.download'),
          locatorValue: 'download-bar-item',
        },
        {
          action: () => { this.openShareOriginalsPane(this.selection) },
          icon: 'share',
          name: 'share',
          title: this.$t('common.share'),
          locatorValue: 'share-bar-item',
        },
        {
          action: () => { this.onAddToArchives() },
          icon: 'inventory_2',
          locatorValue: 'archive-bar-item',
          name: 'archive',
          title: this.$t('common.archives.ArchiveAddAccess.text'),
        },
        {
          action: () => { this.openDeleteOriginalsDialog(this.selection) },
          icon: 'delete',
          locatorValue: 'delete-bar-item',
          name: 'delete',
          style: 'text-error',
          title: this.$t('common.delete'),
          variant: 'danger',
        },
        {
          action: () => { this.onExportToImanage() },
          iconComponent: 'app-icon-imanage',
          hidden: !this.imanageEnabledAndLogged,
          locatorValue: 'imanage-export-bar-item',
          name: 'imanage',
          title: this.$t('project.signed-documents.SignedDocumentsActionBar.exportToImanage'),
        },
        {
          action: () => { this.onExportToPoly() },
          iconComponent: 'app-icon-poly-office',
          hidden: !this.polyOfficeEnabled,
          locatorValue: 'poly-export-bar-item',
          name: 'poly',
          title: this.$t('common.polyOffice.PolyOfficeMultiSelectBarButtonExport.exportFiles'),
        },
      ]
    },
    archiveAddRouteOptions () {
      return {
        state: {
          signedDocuments: this.selection,
        },
        query: {
          signedDocumentIds: this.selection.map((signedDocument) => signedDocument.id).toString(),
          fromMnemo: this.roomMnemo,
        },
      }
    },
    documentSelectionSize () {
      return this.selection.reduce((accSum, item) => {
        return accSum + (item.signSize + item.proofSize)
      }, 0)
    },
    selectedSignatures: {
      get () {
        return this.selection
      },
      set (val) {
        this.$emit('selectedSignature', val)
      },
    },
  },
  methods: {
    ...mapActions('polyOffice', [GET_EXPORT_IFRAME]),
    exportToImanage () {
      this.$emit('exportToImanage', this.selection.map(item => item.id))
    },
    onAddToArchives () {
      this.$router.push(
        {
          name: ARCHIVES_ADD_ROUTE_NAME,
          ...this.archiveAddRouteOptions,
        },
      )
    },
    onBibleGenerateDialogClose () {
      this.openBibleGenerateDialog = false
    },
    onClearSelection () {
      this.$emit('deselect-all')
    },
    onDownloadSignedDocumentsClick () {
      if (this.selection.length > 1) {
        this.openBibleGenerateDialog = true
      } else if (this.selection.length === 1) {
        this.openSingleFileDownloadDialog = true
      }
    },
    onExportToImanage () {
      this.openExportToIManageDialog(this.selection.map(item => item.id))
    },
    onSelectedSignatures (selectedSignatures) {
      this.selectedSignatures = selectedSignatures
      this.$router.push({ name: ROOM_BIBLES_ROUTE_NAME, params: { roomType: getRoomRouteType(this.currentRoom) }, state: { poll: true } })
    },
    preparePolyOfficePayload () {
      const signedEnvIds = this.selection.reduce((acc, item) => {
        return [
          ...acc,
          item.id,
        ]
      }, [])

      return new SignedEnvExport(this.roomMnemo, signedEnvIds)
    },
    async polyOfficeOpenDialogHandler () {
      const payload = this.preparePolyOfficePayload()
      try {
        await this.GET_EXPORT_IFRAME({
          payload,
          successMessage: this.$t('project.signed-documents.SignedDocumentsActionBar.polyOffice.exportSuccessful'),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.polyOfficeErrors.ERR_FILE_TOO_LARGE', 1))
      }
    },
  },
}
</script>